
import { createContext, useContext, ReactNode, useState } from 'react';
import { LanguageContextType, SupportedLanguage } from '@/types/language';
import { useLanguageManager } from '@/hooks/useLanguageManager';
import { getTranslation } from '@/utils/translations';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

// Create the context with a default value to prevent the "must be used within a Provider" error
const LanguageContext = createContext<LanguageContextType>({
  language: 'zh-TW', 
  setLanguage: () => {},
  t: (key: string) => key,
});

// Provider component that wraps the app
export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const { language, setLanguage } = useLanguageManager();

  const t = (key: string, options?: { [key: string]: any }): string => {
    return getTranslation(language, key, options);
  };

  // Create the context value object
  const value: LanguageContextType = {
    language,
    setLanguage,
    t,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

// Custom hook to use the language context
export const useLanguage = () => {
  const context = useContext(LanguageContext);
  // We'll no longer throw an error since we have a default value
  // This will allow the app to continue running even if used outside a provider
  return context;
};

// Language switcher component
export const LanguageSwitcher = () => {
  const { language, setLanguage } = useLanguage();
  
  const languages = [
    { value: 'zh-TW', label: '繁體中文' },
    { value: 'en', label: 'English' }
  ];

  return (
    <Select value={language} onValueChange={(value: SupportedLanguage) => setLanguage(value)}>
      <SelectTrigger className="w-[140px]">
        <SelectValue>
          {language === 'en' ? 'English' : '繁體中文'}
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {languages.map((lang) => (
          <SelectItem key={lang.value} value={lang.value}>
            {lang.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};
